import * as React from 'react';
import { Route, RouteComponentProps, RouteProps } from 'react-router';
import { FilterableObject } from 'modules/common/hooks/useFiltered';
import RouteGuard from './RouteGuard';

/**
 * Props for DevsiteRoute, extending react-router's RouteProps with auth, feature flags, and permissions.
 *
 * @see RouteProps for inherited props (path, exact, strict, location, sensitive)
 * @see FilterableObject for inherited filter props (confidence, globalPermission, hidden)
 */
export interface DevsiteRouteProps
  extends RouteProps,
    Pick<FilterableObject, 'confidence' | 'globalPermission' | 'hidden'> {
  /** Page title to set when this route is active */
  title?: string;
  /** If true, renders 403 error page. Used internally by OrgPermissionedRoute for permission failures. */
  notAuthorized?: boolean;
  /** If true, allows unauthenticated access. Default: false (requires auth, redirects to login if not signed in). */
  isPublic?: boolean;
}

/**
 * Primary routing component for the Developer Site. Wrapper around react-router `<Route>` that handles
 * authentication (by default), feature flags, confidence levels, and global permissions.
 *
 * **Authentication:** By default, this component **REQUIRES authentication** and redirects to login if not signed in.
 * Use the `isPublic` prop to allow unauthenticated access.
 *
 * **When to use:**
 * - **Most routes** - This should be your default choice for routing (auth is enforced by default)
 * - **Hybrid/public routes** - Use `isPublic` prop for routes accessible logged in or out
 *
 * **When NOT to use:**
 * - **Routes requiring org-level permissions** → use `OrgPermissionedRoute` instead (adds permission checking)
 *
 * **Access checks:** Delegated to `RouteGuard`, which documents the props and the order the checks run in.
 * `hidden`, `confidence`, `globalPermission`, and `notAuthorized` are passed straight through to it.
 *
 * **Usage Examples:**
 *
 * ```tsx
 * // Protected route (default - requires auth)
 * <DevsiteRoute path={routes.console} component={ConsoleApp} />
 *
 * // Hybrid/public route (accessible logged in or out)
 * <DevsiteRoute path={routes.documentation} component={Documentation} isPublic />
 *
 * // Internal route (inside already-protected area - auth not rechecked)
 * <DevsiteRoute path={routes.adminUsers} component={UserManagement} />
 *
 * // Feature-flagged route
 * <DevsiteRoute
 *   path={routes.newFeature}
 *   component={NewFeature}
 *   hidden={!TOGGLE_NEW_FEATURE}
 * />
 *
 * // Route with global permission check
 * <DevsiteRoute
 *   path={routes.adminTool}
 *   component={AdminTool}
 *   globalPermission={GlobalPermission.ADMIN_ACCESS}
 * />
 * ```
 *
 * @param props.hidden - If true, renders 404 instead of component (typically used for feature flags)
 * @param props.confidence - Confidence level required for route to be visible
 * @param props.globalPermission - Global (platform-wide) permission required. Shows 403 if user lacks it.
 * @param props.notAuthorized - If true, explicitly renders 403 (used internally by OrgPermissionedRoute)
 * @param props.component - Component to render for this route
 * @param props.render - Alternative to component prop for render function
 * @param props.title - Page title to set when route is active
 * @returns A `<Route>` that renders the component inside a `RouteGuard`. The guard redirects to login or shows the
 * 403/404 page instead when a check fails.
 *
 * The checks run only while the route matches, so `exact` and `strict` apply to them. Use it inside a `Switch` or
 * `Routing` so a failing route falls through to the next match or the catch-all 404.
 */
const DevsiteRoute: React.FC<React.PropsWithChildren<DevsiteRouteProps>> = (props) => {
  // children is kept out of rest: <Route> prefers children over render, which would skip RouteGuard
  const { confidence, globalPermission, component, render, title, hidden, notAuthorized, isPublic, children, ...rest } =
    props;

  const componentRender = (renderProps: RouteComponentProps) => (
    <RouteGuard
      confidence={confidence}
      globalPermission={globalPermission}
      hidden={hidden}
      notAuthorized={notAuthorized}
      isPublic={isPublic}
      title={title}
    >
      <RouteRender component={component} render={render} renderProps={renderProps}>
        {children}
      </RouteRender>
    </RouteGuard>
  );

  // SENTRY: There's room here to use the Sentry.withSentryRouting, but it'd be potentially easier with react-router v6.
  // Without v6, this appears to rerender things on every link click.
  return <Route {...rest} render={componentRender} />;
};

interface RouteRenderProps {
  component?: React.ComponentType<RouteComponentProps>;
  render?: (props: RouteComponentProps) => React.ReactNode;
  renderProps: RouteComponentProps;
}

export const RouteRender: React.FC<React.PropsWithChildren<RouteRenderProps>> = (props) => {
  if (props.children) return <>{props.children}</>;
  if (props.component) return <props.component {...props.renderProps} />;
  // Nothing to render when no component, render or children is given, rather than throwing
  return <>{props.render?.(props.renderProps)}</>;
};

export default DevsiteRoute;
